import { SECTION_IDS, type SectionId } from "../types";

/* ============================================================================
 * ПАРТИТУРА МАРШРУТА — единственное место, где живёт время второй половины сайта.
 *
 * Читается так. Каждый блок проходит через кадр в три фазы:
 *
 *      ВЪЕЗД            УДЕРЖАНИЕ                    ВЫЕЗД
 *   блок поднимается   блок стоит неподвижно,     блок уезжает
 *   снизу экрана       здесь и только здесь        вверх
 *                      играет его анимация
 *   ──────────────► ├───────────────────────────┤ ──────────────►
 *      1 экран        `hold` экранов                1 экран
 *
 * Высота секции = (1 + hold) экрана. Въезд и выезд — это обычная прокрутка,
 * общая с соседями: пока один уезжает, следующий уже поднимается. Никаких
 * затуханий: блок не гаснет и не «включается», он приезжает и уезжает.
 *
 * Внутри удержания есть ещё два порога:
 *
 *   ├─ settle ─┼──────── сцена играет ────────┼─ dwell ─┤
 *   0         settle                       resolve      1
 *
 *   settle   доля удержания, пока блок только встал: он уже неподвижен и
 *            читается, но анимация ещё не началась. Даёт глазу собраться.
 *   resolve  доля удержания, к которой анимация ОБЯЗАНА досмотреться. Всё,
 *            что после, — удержание готового состояния (dwell). Без него
 *            последний такт любой последовательности виден полсекунды:
 *            измерено на «Бери простые фриланс-заказы», где узел «Выплата»
 *            попадал в читаемое окно на 50px из 825.
 *
 * Блок получает наружу `stage` — 0 в момент settle, 1 в момент resolve. Вся
 * анимация блока написана в этих 0..1 и ничего не знает про прокрутку. Поэтому
 * менять тайминг блока можно здесь, не трогая сам блок.
 *
 * КАК МЕНЯТЬ:
 *   блок пролетает слишком быстро            → увеличить `hold`
 *   последний такт мелькает                  → уменьшить `resolve`
 *   анимация стартует, пока блок ещё едет    → увеличить `settle`
 *   блок вообще не должен прилипать          → hold: 0
 *
 * `beats` — сколько смысловых тактов внутри блока. Это не влияет на рендер,
 * но по нему считается, сколько экрана достаётся одному такту, и проверка
 * ниже ругается, если такту досталось меньше минимума.
 * ==========================================================================*/

export interface SectionScore {
  /**
   * Блок прилипает к кадру на время удержания.
   * false — либо контент выше экрана и прилипание его обрежет, либо у блока
   * собственный движок удержания (так у «Создавай 3D-объекты»: пять тактов
   * держат сами себя, и ломать это ради единообразия нельзя).
   */
  readonly pin: boolean;
  /** Экранов удержания. 0 — блок не прилипает, просто прокручивается. */
  readonly hold: number;
  /** Доля удержания до старта анимации. */
  readonly settle: number;
  /** Доля удержания, к которой анимация досмотрена. */
  readonly resolve: number;
  /** Смысловых тактов внутри блока (для расчёта и проверки). */
  readonly beats: number;
  /** Зачем блоку столько времени — человеческим языком. */
  readonly note: string;
}

/** Минимум экрана на один такт, ниже которого такт не успевают прочитать. */
export const MIN_SCREEN_PER_BEAT = 0.3;

export const SECTION_SCORE: Readonly<Record<SectionId, SectionScore>> = Object.freeze({
  workflow: {
    pin: false, hold: 4.25, settle: 0.02, resolve: 0.98, beats: 5,
    note: "Пять тактов поверх неподвижной пары окон. Держит их собственный редакционный движок — здесь только длина, из которой считается такт.",
  },
  where: {
    pin: false, hold: 1.1, settle: 0.10, resolve: 0.78, beats: 1,
    note: "Хвост той же сцены, что и «Создавай 3D-объекты»: те же два окна, "
      + "тот же дописывающийся чат. Прилипание здесь разорвало бы пару, "
      + "поэтому блок проходит кадр целиком, как и его начало.",
  },
  "uses-game": {
    pin: true, hold: 3.2, settle: 0.06, resolve: 0.90, beats: 7,
    note: "Заголовок и окно приезжают, затем куб входит в туннель, выполняет боковые шаги, одинарный, двойной и тройной прыжки, замедление и выход.",
  },
  "uses-site": {
    pin: true, hold: 1.6, settle: 0.08, resolve: 0.80, beats: 2,
    note: "Разворот миниатюры в полный экран — единственное движение, ему нужен запас.",
  },
  "uses-video": {
    pin: true, hold: 1.1, settle: 0.10, resolve: 0.74, beats: 2,
    note: "Два ролика приезжают и остаются доступными для запуска.",
  },
  "uses-freelance": {
    pin: true, hold: 2.0, settle: 0.08, resolve: 0.72, beats: 4,
    note: "Четыре узла цепочки. Последний обязан достояться, а не мелькнуть на выезде.",
  },
  "uses-more": {
    pin: true, hold: 2.0, settle: 0.08, resolve: 0.72, beats: 4,
    note: "Подсветка идёт по четырём направлениям, затем все четыре остаются зажжёнными.",
  },
  share: {
    pin: true, hold: 1.2, settle: 0.10, resolve: 0.76, beats: 2,
    note: "Окно Share приезжает, сцена внутри собирается из мира сайта.",
  },
  audience: {
    pin: true, hold: 1.5, settle: 0.08, resolve: 0.74, beats: 2,
    note: "Две колонки приходят по очереди, обе должны стоять прочитанными.",
  },
  closer: {
    pin: true, hold: 1.0, settle: 0.08, resolve: 0.70, beats: 1,
    note: "Одна фраза. Приходит быстро и стоит — это точка перед тарифами.",
  },
  pricing: {
    pin: true, hold: 2, settle: 0.08, resolve: 0.90, beats: 4,
    note: "Четыре карточки: чтение, плавный осмотр, годовой тариф и trial. Высокий мобильный контент освобождается раньше, чтобы низ был доступен.",
  },
  install: {
    pin: true, hold: 1.4, settle: 0.08, resolve: 0.90, beats: 2,
    note: "Два пути установки появляются и осматриваются последовательно. Высокий контент сохраняет обычный выезд целиком.",
  },
  footer: {
    pin: true, hold: 1.8, settle: 0, resolve: 1, beats: 2,
    note: "Сетка перестраивается в финальный мир, затем появляется подвал; конец удержания совпадает с концом страницы.",
  },
});

/** Высота секции в экранах: экран под сам кадр плюс удержание. */
export const sectionSpanInScreens = (id: SectionId): number =>
  1 + Math.max(0, SECTION_SCORE[id].hold);

/** Полная длина маршрута в экранах. */
export const routeLengthInScreens = (): number =>
  SECTION_IDS.reduce((total, id) => total + sectionSpanInScreens(id), 0);

export interface StageSample {
  /** Где мы относительно блока. */
  readonly phase: "before" | "enter" | "hold" | "exit" | "after";
  /** Встречное смещение, чтобы кадр стоял неподвижно во время удержания. */
  readonly offsetPx: number;
  /** Ход анимации блока: 0 на settle, 1 на resolve. Это и есть его время. */
  readonly stage: number;
  /** Сырая доля удержания, до пересчёта. Нужна только для отладки. */
  readonly rawHold: number;
  /** Блок сейчас где-то в кадре (въезжает, стоит или уезжает). */
  readonly onStage: boolean;
  /** Блок стоит неподвижно и полностью читается. */
  readonly readable: boolean;
}

const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));

/** Leave enough natural scroll to reveal the bottom of a tall content frame. */
export function sampleContentAwarePin(travelled: number, spanPx: number, viewportHeight: number, contentHeight: number): number {
  const screen = Math.max(1, Number.isFinite(viewportHeight) ? viewportHeight : 1);
  const content = Math.max(screen, Number.isFinite(contentHeight) ? contentHeight : screen);
  const hold = Math.max(0, (Number.isFinite(spanPx) ? spanPx : screen) - content);
  return Math.min(hold, Math.max(0, Number.isFinite(travelled) ? travelled : 0));
}

/**
 * Один блок в один момент прокрутки.
 *
 * `travelled` — сколько пикселей маршрута пройдено с начала секции.
 * Отрицательное значение означает, что блок ещё поднимается снизу.
 */
export function sampleStage(
  id: SectionId,
  travelled: number,
  spanPx: number,
  viewportHeight: number,
): StageSample {
  const score = SECTION_SCORE[id];
  const holdPx = Math.max(0, spanPx - viewportHeight);
  /* Запас в половину экрана с каждой стороны: блок обязан быть уже отрисован
     к моменту, когда его край входит в кадр. Без запаса он «выщёлкивается». */
  const margin = viewportHeight * 0.5;
  const onStage = travelled > -(viewportHeight + margin) && travelled < spanPx + margin;

  if (!score.pin || holdPx <= 0) {
    // Блок не прилипает: его «ход» — это обычное прохождение через кадр.
    const stage = clamp01((travelled + viewportHeight) / Math.max(1, spanPx + viewportHeight));
    return {
      phase: !onStage ? (travelled <= -viewportHeight ? "before" : "after") : "enter",
      offsetPx: 0, stage, rawHold: stage, onStage,
      readable: onStage && travelled >= 0 && travelled <= spanPx - viewportHeight,
    };
  }

  const offsetPx = Math.max(0, Math.min(travelled, holdPx));
  const rawHold = clamp01(travelled / holdPx);
  const span = Math.max(0.0001, score.resolve - score.settle);
  const stage = clamp01((rawHold - score.settle) / span);

  const phase: StageSample["phase"] =
    travelled <= -viewportHeight ? "before"
      : travelled < 0 ? "enter"
        : travelled <= holdPx ? "hold"
          : travelled < spanPx ? "exit"
            : "after";

  return { phase, offsetPx, stage, rawHold, onStage, readable: phase === "hold" };
}

export interface ScoreWarning {
  readonly id: SectionId;
  readonly message: string;
}

/**
 * Проверка партитуры. Ловит ровно те ошибки, из-за которых такты не попадают
 * в кадр, — до того, как их увидит человек.
 */
export function validateScore(): readonly ScoreWarning[] {
  const warnings: ScoreWarning[] = [];
  for (const id of SECTION_IDS) {
    const s = SECTION_SCORE[id];
    if (s.settle < 0 || s.resolve > 1 || s.settle >= s.resolve) {
      warnings.push({ id, message: `settle (${s.settle}) должен быть меньше resolve (${s.resolve}), оба в 0..1` });
    }
    if (s.pin && s.hold > 0 && s.beats > 0) {
      const perBeat = (s.hold * (s.resolve - s.settle)) / s.beats;
      if (perBeat < MIN_SCREEN_PER_BEAT) {
        warnings.push({
          id,
          message: `на такт достаётся ${perBeat.toFixed(2)} экрана при минимуме ${MIN_SCREEN_PER_BEAT}. `
            + `Увеличить hold до ${((MIN_SCREEN_PER_BEAT * s.beats) / (s.resolve - s.settle)).toFixed(2)}`,
        });
      }
    }
    if (s.pin && s.hold === 0) {
      warnings.push({ id, message: "pin: true без удержания — прилипать не к чему" });
    }
  }
  return warnings;
}
